import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  UserPlus,
  Edit,
  Trash2,
  Key,
  Mail,
  Search,
  Loader2,
  Shield,
  User,
  Building2,
  ShoppingCart,
  Store,
  Copy,
  Handshake,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ROLES = [
  { value: "client", label: "Client", icon: User, color: "bg-blue-100 text-blue-800" },
  { value: "buyer", label: "Client", icon: User, color: "bg-blue-100 text-blue-800" },
  { value: "seller", label: "Client", icon: User, color: "bg-blue-100 text-blue-800" },
  { value: "admin", label: "Employé", icon: Shield, color: "bg-brand-100 text-purple-800" },
  { value: "owner", label: "Gestionnaire", icon: Building2, color: "bg-orange-100 text-orange-800" },
  { value: "super_admin", label: "Gestionnaire", icon: Shield, color: "bg-red-100 text-red-800" },
];

// Filter options for the dropdown
const ROLE_FILTERS = [
  { value: "all", label: "Tous les rôles" },
  { value: "client", label: "Client" },
  { value: "partner", label: "Partenaire" },
  { value: "employee", label: "Employé" },
  { value: "manager", label: "Gestionnaire" },
];

const ADMIN_TYPES = [
  { value: "customer_service", label: "Service à la clientèle", description: "État demandes, paiements, billets, renseignements" },
  { value: "technical_support", label: "Support technique", description: "Problèmes techniques, programmation" },
  { value: "both", label: "Les deux", description: "Peut gérer tous les types de demandes" },
];

const getRoleBadge = (role) => {
  const roles = (role || '').split(',').map(r => r.trim());
  const isPartner = roles.includes('partner');
  const mainRole = roles.find(r => r !== 'partner') || roles[0];
  const roleInfo = ROLES.find((r) => r.value === mainRole) || ROLES[0];
  return (
    <div className="flex items-center gap-1">
      <Badge className={`${roleInfo.color} flex items-center gap-1`}>
        <roleInfo.icon className="h-3 w-3" />
        {roleInfo.label}
      </Badge>
      {isPartner && (
        <Badge className="bg-brand-100 text-brand-700 flex items-center gap-1">
          <Handshake className="h-3 w-3" />
          Partenaire
        </Badge>
      )}
    </div>
  );
};

const getAdminTypeBadge = (adminType) => {
  if (!adminType) return null;
  const typeInfo = ADMIN_TYPES.find((t) => t.value === adminType);
  if (!typeInfo) return null;
  const colors = {
    customer_service: "bg-blue-100 text-blue-700",
    technical_support: "bg-purple-100 text-purple-700",
    both: "bg-green-100 text-green-700"
  };
  return (
    <Badge className={`${colors[adminType] || 'bg-gray-100 text-gray-700'} text-xs`}>
      {typeInfo.label}
    </Badge>
  );
};

const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form states
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "buyer",
    admin_type: ""
  });
  const [editUser, setEditUser] = useState({
    email: "",
    name: "",
    role: "",
    admin_type: ""
  });
  const [newPassword, setNewPassword] = useState("");

  // Promote to partner state
  const [showPromotePartnerDialog, setShowPromotePartnerDialog] = useState(false);
  const [promotePartnerData, setPromotePartnerData] = useState({
    partner_type: "admin_groupe",
    promo_code: "",
    company_name: "",
    facebook_group_name: "",
    facebook_group_url: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users?page=${currentPage}&limit=${PAGE_SIZE}`);
      const data = response.data;
      // Handle both paginated and legacy response
      if (data.users) {
        setUsers(data.users);
        setTotalPages(data.pages || 1);
        setTotalUsers(data.total || data.users.length);
      } else if (Array.isArray(data)) {
        setUsers(data);
        setTotalPages(1);
        setTotalUsers(data.length);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    }
    setLoading(false);
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      user.email.toLowerCase().includes(searchLower) ||
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.client_number && user.client_number.toLowerCase().includes(searchLower));
    
    // Role filter logic: client = buyer/seller, employee = admin, manager = owner/super_admin, partner = has partner in role
    let matchesRole = roleFilter === "all";
    if (roleFilter === "client") {
      matchesRole = user.role === "buyer" || user.role === "seller" || user.role === "client";
    } else if (roleFilter === "partner") {
      matchesRole = (user.role || '').includes("partner");
    } else if (roleFilter === "employee") {
      matchesRole = user.role === "admin";
    } else if (roleFilter === "manager") {
      matchesRole = user.role === "owner" || user.role === "super_admin";
    }
    
    return matchesSearch && matchesRole;
  });

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (newUser.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success("Utilisateur créé avec succès");
      setShowAddDialog(false);
      setNewUser({ email: "", password: "", name: "", role: "buyer" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
    setActionLoading(false);
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    const updateData = {};
    if (editUser.email && editUser.email !== selectedUser.email) {
      updateData.email = editUser.email;
    }
    if (editUser.name && editUser.name !== selectedUser.name) {
      updateData.name = editUser.name;
    }
    if (editUser.role && editUser.role !== selectedUser.role) {
      updateData.role = editUser.role;
    }

    if (Object.keys(updateData).length === 0) {
      toast.info("Aucune modification détectée");
      setShowEditDialog(false);
      return;
    }

    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData);
      toast.success("Utilisateur mis à jour");
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
    setActionLoading(false);
  };

  const handleDeleteUser = async (user) => {
    try {
      await axios.delete(`${API}/admin/users/${user.id}`);
      toast.success(`Utilisateur ${user.email} supprimé`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success("Mot de passe réinitialisé");
      setShowResetPasswordDialog(false);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    }
    setActionLoading(false);
  };

  const handleSendResetEmail = async (user) => {
    try {
      await axios.post(`${API}/admin/users/${user.id}/send-reset-email`);
      toast.success(`Email de réinitialisation envoyé à ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const openPromotePartnerDialog = (user) => {
    setSelectedUser(user);
    setPromotePartnerData({
      partner_type: "admin_groupe",
      promo_code: "",
      company_name: "",
      facebook_group_name: "",
      facebook_group_url: "",
    });
    setShowPromotePartnerDialog(true);
  };

  const handlePromotePartner = async () => {
    if (!selectedUser || !promotePartnerData.promo_code.trim()) {
      toast.error("Le code promo est requis");
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/promote-partner`, promotePartnerData);
      toast.success(`${selectedUser.email} est maintenant partenaire!`);
      setShowPromotePartnerDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la promotion");
    }
    setActionLoading(false);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      email: user.email,
      name: user.name,
      role: user.role,
      admin_type: user.admin_type || ''
    });
    setShowEditDialog(true);
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowResetPasswordDialog(true);
  };

  return (
    <div className="space-y-4" data-testid="user-management-container">
      {/* Header with search and add button */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher par nom, email ou numéro client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40" data-testid="role-filter-select">
              <SelectValue placeholder="Filtrer par rôle" />
            </SelectTrigger>
            <SelectContent>
              {ROLE_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-brand-600 hover:bg-brand-700" data-testid="add-user-button">
              <UserPlus className="h-4 w-4 mr-2" />
              Ajouter un utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Créez un nouveau compte utilisateur
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom complet</Label>
                <Input
                  placeholder="Jean Dupont"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="jean@exemple.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Mot de passe</Label>
                <Input
                  type="password"
                  placeholder="Minimum 8 caractères"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddUser} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      ) : (
      <>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Numéro client</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Date de création</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-brand-600" />
                        </div>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.client_number ? (
                        <span 
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 text-white font-mono font-bold text-sm shadow-sm cursor-pointer hover:shadow-md transition-all group"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await navigator.clipboard.writeText(user.client_number);
                              toast.success("Numéro client copié!");
                            } catch (err) {
                              toast.error("Impossible de copier");
                            }
                          }}
                          title="Cliquez pour copier"
                        >
                          {user.client_number}
                          <Copy className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-gray-500">
                      {new Date(user.created_at).toLocaleDateString("fr-CA")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openResetPasswordDialog(user)}
                          title="Réinitialiser le mot de passe"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSendResetEmail(user)}
                          title="Envoyer email de réinitialisation"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        {/* Promote to partner - only for non-partner users */}
                        {!(user.role || '').includes('partner') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPromotePartnerDialog(user)}
                            title="Promouvoir en partenaire"
                            className="text-brand-600 hover:text-brand-700 hover:bg-brand-50"
                            data-testid={`promote-partner-btn-${user.id}`}
                          >
                            <Handshake className="h-4 w-4" />
                          </Button>
                        )}
                        {user.id !== currentUser?.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Êtes-vous sûr de vouloir supprimer l'utilisateur{" "}
                                  <strong>{user.email}</strong>? Cette action est irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(user)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="users-prev-page"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <span className="text-sm text-gray-500">
              Page {currentPage} / {totalPages} ({totalUsers} utilisateurs)
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="users-next-page"
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>
              Modifiez les informations de {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom complet</Label>
              <Input
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select
                value={editUser.role}
                onValueChange={(value) => setEditUser({ ...editUser, role: value, admin_type: value === 'admin' || value === 'super_admin' ? editUser.admin_type : '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Admin Type - only show for admin roles */}
            {(editUser.role === 'admin' || editUser.role === 'super_admin') && (
              <div className="space-y-2">
                <Label>Type d'admin (chat live)</Label>
                <Select
                  value={editUser.admin_type || ''}
                  onValueChange={(value) => setEditUser({ ...editUser, admin_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ADMIN_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-gray-500">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Détermine quel type de demandes de chat live cet admin peut gérer
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleEditUser} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>
              Définissez un nouveau mot de passe pour {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                placeholder="Minimum 8 caractères"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleResetPassword} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote to Partner Dialog */}
      <Dialog open={showPromotePartnerDialog} onOpenChange={setShowPromotePartnerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="h-5 w-5 text-brand-600" />
              Promouvoir en partenaire
            </DialogTitle>
            <DialogDescription>
              Promouvoir <strong>{selectedUser?.email}</strong> au rôle de partenaire Middleman
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de partenaire *</Label>
              <Select
                value={promotePartnerData.partner_type}
                onValueChange={(value) => setPromotePartnerData({ ...promotePartnerData, partner_type: value })}
              >
                <SelectTrigger data-testid="promote-partner-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin_groupe">Admin de groupe Facebook</SelectItem>
                  <SelectItem value="revendeur">Revendeur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Code promo unique *</Label>
              <Input
                placeholder="Ex: GROUPE-MTL, REVENTE-QC..."
                value={promotePartnerData.promo_code}
                onChange={(e) => setPromotePartnerData({ ...promotePartnerData, promo_code: e.target.value.toUpperCase() })}
                className="font-mono uppercase"
                data-testid="promote-partner-promo-code"
              />
              <p className="text-xs text-gray-500">Ce code sera partagé par le partenaire pour recruter des filleuls</p>
            </div>
            {promotePartnerData.partner_type === "admin_groupe" && (
              <>
                <div className="space-y-2">
                  <Label>Nom du groupe Facebook</Label>
                  <Input
                    placeholder="Ex: Fans des Canadiens"
                    value={promotePartnerData.facebook_group_name}
                    onChange={(e) => setPromotePartnerData({ ...promotePartnerData, facebook_group_name: e.target.value })}
                    data-testid="promote-partner-fb-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL du groupe Facebook</Label>
                  <Input
                    placeholder="https://facebook.com/groups/..."
                    value={promotePartnerData.facebook_group_url}
                    onChange={(e) => setPromotePartnerData({ ...promotePartnerData, facebook_group_url: e.target.value })}
                    data-testid="promote-partner-fb-url"
                  />
                </div>
              </>
            )}
            {promotePartnerData.partner_type === "revendeur" && (
              <div className="space-y-2">
                <Label>Nom de la compagnie</Label>
                <Input
                  placeholder="Ex: Billets Express Inc."
                  value={promotePartnerData.company_name}
                  onChange={(e) => setPromotePartnerData({ ...promotePartnerData, company_name: e.target.value })}
                  data-testid="promote-partner-company"
                />
              </div>
            )}
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-brand-800">Commissions par défaut:</p>
              <ul className="text-brand-700 mt-1 space-y-0.5">
                <li>25% sur le 1er transfert d'un filleul</li>
                <li>5% sur les transferts récurrents</li>
                <li>25% de réduction pour les membres référés</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromotePartnerDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handlePromotePartner} 
              disabled={actionLoading || !promotePartnerData.promo_code.trim()}
              className="bg-brand-600 hover:bg-brand-700"
              data-testid="confirm-promote-partner-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Handshake className="h-4 w-4 mr-2" />}
              Promouvoir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
